/**
 * Модуль для работы со слайдерами
 */
import Swiper from 'swiper/bundle'

/**
 * Инициализирует простой слайдер
 * @param {Object} options - Опции слайдера
 */
export function initSlider({
  containerSelector,
  trackSelector,
  slideSelector,
  prevButtonSelector,
  nextButtonSelector,
  dotsContainerSelector,
  itemsPerView = { desktop: 4, tablet: 3, mobile: 2, small: 1 },
  breakpoints = { desktop: 992, tablet: 768, mobile: 576 },
}) {
  const container = document.querySelector(containerSelector)
  if (!container) return

  const track = container.querySelector(trackSelector)
  const slides = container.querySelectorAll(slideSelector)
  const prevBtn = container.querySelector(prevButtonSelector)
  const nextBtn = container.querySelector(nextButtonSelector)
  const dotsContainer = container.querySelector(dotsContainerSelector)

  if (!track || !slides.length) return

  let currentIndex = 0
  let itemsToShow = getItemsPerView(window.innerWidth, itemsPerView, breakpoints)
  let slideWidth = getSlideWidth()

  function getItemsPerView(width, items, breakpoints) {
    if (width >= breakpoints.desktop) return items.desktop
    if (width >= breakpoints.tablet) return items.tablet
    if (width >= breakpoints.mobile) return items.mobile
    return items.small
  }

  function getSlideWidth() {
    if (!slides[0]) return 0
    const style = window.getComputedStyle(track)
    const gap = parseFloat(style.gap) || 0
    return slides[0].offsetWidth + gap
  }

  function updateSlider() {
    slideWidth = getSlideWidth()
    track.style.transform = `translateX(-${currentIndex * slideWidth}px)`

    if (prevBtn) {
      prevBtn.disabled = currentIndex === 0
      prevBtn.style.opacity = currentIndex === 0 ? '0.5' : '1'
    }

    if (nextBtn) {
      const maxIndex = slides.length - itemsToShow
      nextBtn.disabled = currentIndex >= maxIndex
      nextBtn.style.opacity = currentIndex >= maxIndex ? '0.5' : '1'
    }

    updateDots()
  }

  function createDots() {
    if (!dotsContainer) return

    dotsContainer.innerHTML = ''
    const maxIndex = slides.length - itemsToShow
    const dotsCount = maxIndex + 1

    for (let i = 0; i < dotsCount; i++) {
      const dot = document.createElement('button')
      dot.className = `${containerSelector.replace('.', '')}__dot`
      if (i === currentIndex) dot.classList.add('active')
      dot.setAttribute('aria-label', `Слайд ${i + 1}`)
      dot.addEventListener('click', () => {
        currentIndex = i
        updateSlider()
      })
      dotsContainer.appendChild(dot)
    }
  }

  function updateDots() {
    if (!dotsContainer) return
    const dots = dotsContainer.querySelectorAll('button')
    dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === currentIndex)
    })
  }

  function updateItemsToShow() {
    itemsToShow = getItemsPerView(window.innerWidth, itemsPerView, breakpoints)
    const maxIndex = slides.length - itemsToShow
    if (currentIndex > maxIndex) {
      currentIndex = Math.max(0, maxIndex)
    }
    updateSlider()
    createDots()
  }

  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      if (currentIndex > 0) {
        currentIndex--
        updateSlider()
      }
    })
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      const maxIndex = slides.length - itemsToShow
      if (currentIndex < maxIndex) {
        currentIndex++
        updateSlider()
      }
    })
  }

  window.addEventListener('resize', () => {
    updateItemsToShow()
  })

  updateItemsToShow()
}

/**
 * Инициализирует слайдер преподавателей
 */
export function initTeachersSlider() {
  const slider = document.querySelector('.teachers__grid')
  const prevBtn = document.querySelector('.teachers__arrow--prev')
  const nextBtn = document.querySelector('.teachers__arrow--next')

  if (!slider || !prevBtn || !nextBtn) return

  const slides = slider.querySelectorAll('.teacher-card')
  if (slides.length === 0) return

  let slideWidth = slides[0].offsetWidth + parseFloat(getComputedStyle(slides[0]).marginRight)
  let visibleSlides = getVisibleSlidesCount()
  let currentIndex = 0
  let maxIndex = slides.length - visibleSlides

  function getVisibleSlidesCount() {
    const viewportWidth = window.innerWidth
    if (viewportWidth >= 992) return 4
    if (viewportWidth >= 768) return 3
    if (viewportWidth >= 576) return 2
    return 1
  }

  function updateSliderPosition() {
    slider.style.transform = `translateX(-${currentIndex * slideWidth}px)`

    prevBtn.disabled = currentIndex === 0
    prevBtn.style.opacity = currentIndex === 0 ? '0.5' : '1'

    nextBtn.disabled = currentIndex >= maxIndex
    nextBtn.style.opacity = currentIndex >= maxIndex ? '0.5' : '1'
  }

  window.addEventListener('resize', () => {
    slideWidth = slides[0].offsetWidth + parseFloat(getComputedStyle(slides[0]).marginRight)
    visibleSlides = getVisibleSlidesCount()
    maxIndex = Math.max(0, slides.length - visibleSlides)

    if (currentIndex > maxIndex) {
      currentIndex = maxIndex
    }
    updateSliderPosition()
  })

  prevBtn.addEventListener('click', () => {
    if (currentIndex > 0) {
      currentIndex--
      updateSliderPosition()
    }
  })

  nextBtn.addEventListener('click', () => {
    if (currentIndex < maxIndex) {
      currentIndex++
      updateSliderPosition()
    }
  })

  updateSliderPosition()
}

/**
 * Инициализирует слайдер отзывов через Swiper
 */
export function initTestimonialsSlider() {
  const el = document.querySelector('.testimonials__swiper')
  if (!el) return

  const swiper = new Swiper(el, {
    slidesPerView: 1,
    spaceBetween: 24,
    loop: false,
    grabCursor: true,
    a11y: { enabled: true },

    // Пагинация вне .swiper — dynamicBullets не уходит за край при 30+ отзывах
    pagination: {
      el: '.testimonials__pagination',
      clickable: true,
      dynamicBullets: true,
      dynamicMainBullets: 3,
    },

    // Кнопки тоже вне .swiper
    navigation: {
      prevEl: '.testimonials__btn-prev',
      nextEl: '.testimonials__btn-next',
    },

    breakpoints: {
      640:  { slidesPerView: 1, spaceBetween: 20, pagination: {dynamicMainBullets: 1} },
      768:  { slidesPerView: 2, spaceBetween: 24, pagination: {dynamicMainBullets: 2} },
      1024: { slidesPerView: 3, spaceBetween: 32, pagination: {dynamicMainBullets: 3} },
    },
  })

  // Пауза видео при смене слайда
  swiper.on('slideChange', () => {
    el.querySelectorAll('video').forEach((v) => {
      v.pause()
      const w = v.closest('.testimonial-card__video-wrapper')
      if (w) w.classList.remove('playing')
      v.controls = false
    })
  })

  // Клик на видео
  el.querySelectorAll('.testimonial-card__video-wrapper').forEach((wrapper) => {
    wrapper.addEventListener('click', function () {
      const video = this.querySelector('video')
      if (!video) return
      if (video.paused) {
        video.play()
        this.classList.add('playing')
        video.controls = true
      } else {
        video.pause()
        this.classList.remove('playing')
        video.controls = false
      }
    })
  })
}

/**
 * Инициализирует слайдер мероприятий через Swiper
 */
export function initEventsSlider() {
  const el = document.querySelector('.events__swiper')
  if (!el) return

  new Swiper(el, {
    slidesPerView: 1,
    spaceBetween: 24,
    loop: false,
    grabCursor: true,
    a11y: { enabled: true },

    pagination: {
      el: '.events__pagination',
      clickable: true,
      dynamicBullets: true,
      dynamicMainBullets: 3,
    },

    navigation: {
      prevEl: '.events__btn-prev',
      nextEl: '.events__btn-next',
    },

    breakpoints: {
      640:  { slidesPerView: 1, spaceBetween: 20 },
      768:  { slidesPerView: 2, spaceBetween: 24 },
      1024: { slidesPerView: 3, spaceBetween: 32 },
    },
  })
}

export function initSuccessStoriesSwiper() {
  const el = document.querySelector('.success-stories__swiper')
  if (!el) return

  new Swiper(el, {
    slidesPerView: 1,
    spaceBetween: 24,
    loop: false,
    grabCursor: true,
    a11y: { enabled: true },
    pagination: {
      el: '.success-stories__pagination',
      clickable: true,
      dynamicBullets: true,
      dynamicMainBullets: 1,
    },
    navigation: {
      prevEl: '.success-stories__btn-prev',
      nextEl: '.success-stories__btn-next',
    },
    breakpoints: {
      640:  { slidesPerView: 1, spaceBetween: 20, pagination: { dynamicMainBullets: 1 } },
      768:  { slidesPerView: 2, spaceBetween: 24, pagination: { dynamicMainBullets: 2 } },
      1024: { slidesPerView: 3, spaceBetween: 32, pagination: { dynamicMainBullets: 3 } },
    },
  })
}

